import {
  NewButton,
  NewButtonSize,
  NewButtonVariant,
} from "@visiblethread/react-components";
import clsx from "clsx";
import Flex, { GAP_CLASS } from "components/Flex";
import { ALIGN_CLASS, DIRECTION_CLASS } from "components/Flex/Flex";
import { CHANGE_ITEM_VARIANT } from "layout/activityLog/constants";
import PropTypes from "prop-types";
import React, { useState } from "react";

import styles from "./ChangeList.module.scss";

const VISIBLE_COUNT = 5;
const CHANGE_ITEM_VARIANTS = Object.values(CHANGE_ITEM_VARIANT);

const ChangeListItem = ({ item }) => {
  if (item.variant === CHANGE_ITEM_VARIANT.qa) {
    return (
      <>
        <span className={styles.changeList__question}>{item.question}</span>
        <strong className={styles.changeList__answer}>{item.answer}</strong>
      </>
    );
  }
  return item.text;
};

const ChangeList = ({
  items,
  label = null,
  truncatedCount = 0,
  isSubRow = false,
  className = "",
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (items.length === 0) {
    return null;
  }

  const canCollapse = items.length > VISIBLE_COUNT;
  const hiddenWhenCollapsedCount = items.length - VISIBLE_COUNT;
  const visibleItems = isExpanded ? items : items.slice(0, VISIBLE_COUNT);
  const moreRowVariant = items.some(
    (item) => item.variant === CHANGE_ITEM_VARIANT.bullet
  )
    ? CHANGE_ITEM_VARIANT.bullet
    : CHANGE_ITEM_VARIANT.note;

  const rows = [...visibleItems];
  if (isExpanded && truncatedCount > 0) {
    rows.push({
      key: "__truncated",
      variant: moreRowVariant,
      text: `+${truncatedCount} more`,
    });
  }

  return (
    <Flex
      direction={DIRECTION_CLASS.column}
      alignItems={ALIGN_CLASS["flex-start"]}
      gap={GAP_CLASS.GAP_100}
      className={clsx(
        styles.changeList,
        isSubRow && styles["changeList--subRow"],
        className
      )}
    >
      {label && <span className={styles.changeList__label}>{label}</span>}
      <Flex
        as="ul"
        direction={DIRECTION_CLASS.column}
        alignItems={ALIGN_CLASS["flex-start"]}
        gap={GAP_CLASS.GAP_050}
        className={styles.changeList__items}
      >
        {rows.map((item) => (
          <Flex
            as="li"
            key={item.key}
            alignItems={ALIGN_CLASS.center}
            gap={GAP_CLASS.GAP_200}
            className={clsx(
              styles.changeList__item,
              styles[`changeList__item--${item.variant}`]
            )}
          >
            <ChangeListItem item={item} />
          </Flex>
        ))}
      </Flex>
      {canCollapse && (
        <NewButton
          variant={NewButtonVariant.l4}
          size={NewButtonSize.sm}
          onClick={() => setIsExpanded((prev) => !prev)}
          aria-label={
            isExpanded
              ? "Show fewer changes"
              : `Show ${hiddenWhenCollapsedCount} more changes`
          }
        >
          {isExpanded ? "Show less" : `Show ${hiddenWhenCollapsedCount} more`}
        </NewButton>
      )}
    </Flex>
  );
};

const changeListItemShape = {
  variant: PropTypes.oneOf(CHANGE_ITEM_VARIANTS).isRequired,
  text: PropTypes.node,
  question: PropTypes.node,
  answer: PropTypes.node,
};

ChangeListItem.propTypes = {
  item: PropTypes.shape(changeListItemShape).isRequired,
};

ChangeList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      ...changeListItemShape,
    })
  ).isRequired,
  label: PropTypes.string,
  truncatedCount: PropTypes.number,
  isSubRow: PropTypes.bool,
  className: PropTypes.string,
};

export default ChangeList;
